<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { expanded: !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded header-height">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link class="navbar-brand" to="/">
              <span v-if="!isVerticalMenuCollapsed || isMouseHovered" class="brand-logo-full">
                <b-img :src="logoFull" alt="logoFull" />
              </span>
              <span v-else class="brand-logo">
                <b-img :src="logoShort" alt="logoShort" />
              </span>
              <!-- <h2 class="brand-text">
                {{ appName }}
              </h2> -->
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon icon="XIcon" size="20" class="d-block d-xl-none" @click="toggleVerticalMenuActive" />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        (evt) => {
          shallShadowBottom = evt.srcElement.scrollTop > 0
        }
      "
    >
      <vertical-nav-menu-items :items="navMenuItems" class="navigation navigation-main" />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->

    <div v-if="supportDisplay" class="support-link">
      <ul class="navigation navigation-main">
        <li class="nav-item">
          <b-link
            class="d-flex align-items-center"
            :href="supportHelpLink"
            target="_blank"
          >
            <feather-icon icon="HelpCircleIcon" />
            <span class="menu-title text-truncate">{{ $t('navigation.support.help') | capitalize }}</span>
          </b-link>
        </li>
        <li class="nav-item">
          <b-link
            class="d-flex align-items-center"
            :href="supportContactLink"
            target="_blank"
          >
            <feather-icon icon="LifeBuoyIcon" />
            <span class="menu-title text-truncate">{{ $t('navigation.support.contact') | capitalize }}</span>
          </b-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { provide, computed, ref, watch, onMounted } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import navMenuItems from '@/navigation/navigationItems'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  name: 'VerticalNavMenu',

  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props, ctx) {
    const { $store } = ctx.root
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, logoShort, logoFull } = $themeConfig.app

    const selectedOrganization = computed(() => $store.state.auth.selectedOrganization)
    const supportDisplay = ref(false)
    const supportContactLink = ref(null)
    const supportHelpLink = ref(null)

    // Fonction qui permet de changer les liens en fonction de l'organisation sélectionnée
    const setSupportHelpLink = function (selectedOrganization) {
      if (selectedOrganization.value.seller) {
        supportContactLink.value = 'https://myflightconnect.atlassian.net/servicedesk/customer/portal/1/group/5'
        supportHelpLink.value = 'https://helpcenter-sellers.myflightconnect.com/'
        supportDisplay.value = true
      } else if (selectedOrganization.value.buyer) {
        supportContactLink.value = 'https://myflightconnect.atlassian.net/servicedesk/customer/portal/1/group/4'
        supportHelpLink.value = 'https://helpcenter-customers.myflightconnect.com/'
        supportDisplay.value = true
      }
    }

    // La première fois on initialise les liens
    onMounted(() => {
      setSupportHelpLink(selectedOrganization)
    })

    // À chaque changement du paramètre d'URL uniqueKey, on réinitialise les liens
    watch(
      () => ctx.root.$route.query.uniqueKey,
      () => {
        setSupportHelpLink(selectedOrganization)
      }
    )

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      logoShort,
      logoFull,
      selectedOrganization,
      supportContactLink,
      supportHelpLink,
      supportDisplay,
    }
  },
}
</script>

<style scoped>
.main-menu {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.scroll-area {
    flex-grow: 1;
    position: relative;
}

.support-link {
    margin-top: auto;
}
</style>

<style lang="scss">
@import '~@core/scss/base/core/menu/menu-types/vertical-menu.scss';
.header-height {
  height: 75px !important;
}
</style>
